import { create } from "zustand";
import { notificationsApi, type NotificationDto } from "@api/modules/notifications.api";
import { createPusherClient, getNotificationChannelName } from "@lib/pusher";
import { createSseClient, resolveSseUrl, type SseClient } from "@lib/sse";
import type Pusher from "pusher-js";
import { useAuthStore } from "@stores/auth/authStore";
import type { BroadcastConfig } from "@lib/broadcast";

export interface NotificationState {
    unreadCount: number;
    notifications: NotificationDto[];
    setUnreadCount: (count: number) => void;
    incrementUnread: () => void;
    addNotification: (n: NotificationDto) => void;
    addRealtimeNotification: (n: NotificationDto) => void;
    removeNotification: (id: string) => void;
    markRead: () => void;
    markDeleted: (wasUnread: boolean) => void;
    fetchUnreadCount: () => Promise<void>;
    subscribeRealtime: (listener: () => void) => () => void;
    pusher: Pusher | null;
    sseClient: SseClient | null;
    startSse: () => void;
    stopSse: () => void;
    startPusher: () => void;
    stopPusher: () => void;
}

const realtimeListeners = new Set<() => void>();
const seenNotificationIds = new Set<string>();
const SEEN_ID_LIMIT = 200;

function emitRealtime() {
    realtimeListeners.forEach((listener) => listener());
}

function rememberNotificationId(id: string): boolean {
    if (seenNotificationIds.has(id)) return false;
    seenNotificationIds.add(id);
    if (seenNotificationIds.size > SEEN_ID_LIMIT) {
        const oldest = seenNotificationIds.values().next().value;
        if (oldest) seenNotificationIds.delete(oldest);
    }
    return true;
}

function mapRealtimePayload(data: Record<string, unknown>): NotificationDto | null {
    const n = (data.notification ?? data) as Record<string, unknown>;
    if (!n?.id) return null;
    return {
        id: String(n.id),
        title: String(n.title ?? ""),
        message: String(n.message ?? ""),
        type: String(n.type ?? "info"),
        data: (n.data as Record<string, unknown>) ?? {},
        url: (n.url as string) ?? null,
        readAt: ((n.readAt ?? n.read_at) as string) ?? null,
        createdAt: ((n.createdAt ?? n.created_at) as string) ?? new Date().toISOString(),
    };
}

function getBroadcastConfig(): BroadcastConfig | null {
    return useAuthStore.getState().broadcast;
}

function shouldProcessPusherEvent(): boolean {
    if (typeof document === "undefined") return true;
    return document.visibilityState === "hidden";
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
    unreadCount: 0,
    notifications: [],
    setUnreadCount: (count) => set({ unreadCount: Math.max(0, count) }),
    incrementUnread: () => set((s) => ({ unreadCount: s.unreadCount + 1 })),
    addNotification: (n) =>
        set((s) => ({
            unreadCount: n.readAt ? s.unreadCount : s.unreadCount + 1,
            notifications: [...(s.notifications ?? []), n],
        })),
    addRealtimeNotification: (n) => {
        if (!rememberNotificationId(n.id)) return;
        get().addNotification(n);
        emitRealtime();
    },
    removeNotification: (id) =>
        set((s) => ({
            notifications: (s.notifications ?? []).filter((n) => n.id !== id),
        })),
    markRead: () => set((s) => ({ unreadCount: Math.max(0, s.unreadCount - 1) })),
    markDeleted: (wasUnread: boolean) =>
        set((s) => ({
            unreadCount: wasUnread ? Math.max(0, s.unreadCount - 1) : s.unreadCount,
        })),
    fetchUnreadCount: async () => {
        try {
            const res = await notificationsApi.unreadCount();
            const data = res as { unreadCount?: number };
            set({ unreadCount: data?.unreadCount ?? 0 });
        } catch {
            /* no-op */
        }
    },
    subscribeRealtime: (listener) => {
        realtimeListeners.add(listener);
        return () => {
            realtimeListeners.delete(listener);
        };
    },
    pusher: null,
    sseClient: null,
    startSse: () => {
        const { sseClient, stopSse } = get();
        if (sseClient) stopSse();

        const token = useAuthStore.getState().token;
        if (!token) return;

        const broadcast = getBroadcastConfig();
        const ssePath = broadcast?.sse?.url ?? "/owner/sse";
        const events = broadcast?.sse?.events ?? {
            connected: "connected",
            notification: "notification",
            heartbeat: "heartbeat",
        };

        const client = createSseClient({
            url: resolveSseUrl(ssePath),
            token,
            events,
            onConnected: () => {
                void get().fetchUnreadCount();
            },
            onNotification: (payload) => {
                const dto = mapRealtimePayload(payload);
                if (!dto) return;
                get().addRealtimeNotification(dto);
                void get().fetchUnreadCount();
            },
        });

        set({ sseClient: client });
    },
    stopSse: () => {
        const { sseClient } = get();
        if (sseClient) {
            sseClient.close();
            set({ sseClient: null });
        }
    },
    startPusher: () => {
        const { pusher, stopPusher } = get();
        if (pusher) stopPusher();

        const user = useAuthStore.getState().user;
        const token = useAuthStore.getState().token;
        const broadcast = getBroadcastConfig();
        if (!user?.id || !token || !broadcast?.pusher) return;

        const userId = String(user.id);
        const client = createPusherClient(userId, broadcast.pusher);
        const channelName = broadcast.pusher.channel ?? getNotificationChannelName(userId);
        const notificationEvent =
            broadcast.pusher.event ?? broadcast.platformNotificationEvent ?? "platform.notification";
        const channel = client.subscribe(channelName);

        const handlePusherNotification = (payload: Record<string, unknown>) => {
            if (!shouldProcessPusherEvent()) return;
            const dto = mapRealtimePayload(payload);
            if (!dto) return;
            get().addRealtimeNotification(dto);
            void get().fetchUnreadCount();
        };

        client.connection.bind("connected", () => {
            if (shouldProcessPusherEvent()) {
                void get().fetchUnreadCount();
            }
        });

        channel.bind(notificationEvent, handlePusherNotification);
        channel.bind("notification", handlePusherNotification);
        channel.bind("platform.notification", handlePusherNotification);

        set({ pusher: client });
    },
    stopPusher: () => {
        const { pusher } = get();
        if (pusher) {
            pusher.disconnect();
            set({ pusher: null });
        }
    },
}));
